import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, Button, TextField, Stack, Autocomplete, Chip } from '@mui/material';
import axios from 'axios';
import FileUploadButton from '../FileManager/FileUploadButton';
import moment from 'moment-timezone';

function CreateProjectModal({open, handleClose, addProject, selectedClass, token}) {
    const [fields, setFields] = useState({
        name: "",
        sponsor: "",
        sponsorContact: "",
        description: "",
        tags: [],
        capacity: 6,
    });

    const [fileFormData, setFileFormData] = useState();
    const [endSemester, setEndSemester] = useState("");
    const [newTag, setNewTag] = useState({tag_id: -1, name: ""});
    const [tags, setTags] = useState([]);

    const handleEndSemesterChange = (event, value) => {
        setEndSemester(value);
    };

    const addTag = (value) => {
        if (value === null) return;
        const tag = formatTag(value.name);
        if (tag === "") return;
        if (fields.tags.map((tag) => tag.name).includes(tag)) return;
        setFields({ ...fields, tags: [...fields.tags, {tag_id: value.tag_id, name: tag}] });
    }

    const removeTag = (value) => {
        setFields({ ...fields, tags: fields.tags.filter((tag) => tag.name !== value.name) });
    }

    const handleNewTag = (event) => {
        addTag(newTag);
        setNewTag({
            tag_id: 1,
            name: ""
        });
    }

    const handleTagChange = (value) => {
        if (value?.tag_id)
            setNewTag(value);
        else {
            setNewTag({
                tag_id: -1,
                name: value,
            })
        }
    }

    const handleChange = (prop) => (event) => {
        setFields({ ...fields, [prop]: event.target.value });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const formData = {
            ...fields,
            class_id: selectedClass.class_id,
            startSemester: selectedClass.startSemester,
            startYear: selectedClass.year,
            endSemester: endSemester,
            endYear: selectedClass.startSemester !== "Spring" && endSemester === "Spring" ? selectedClass.year + 1 : selectedClass.year,
            timezone: moment.tz.guess(),
        }

        const response = await axios.post(`${process.env.REACT_APP_API_HOST}/api/projects/create`, formData, 
        {
            headers: {Authorization: `Bearer ${token.access_token}`},
        })

        let projectData = response.data.createdProject;

        projectData["documents"] = [];
        var fileInfo;
        if (fileFormData !== undefined && fileFormData !== null) {
            fileFormData.set('project_id', projectData.project_id);
            fileFormData.set('class_id', selectedClass.class_id);

            fileInfo = await axios.post(`${process.env.REACT_APP_API_HOST}/api/file/uploadProjectFiles`,
            fileFormData,
            {
                headers: {
                    Authorization: `Bearer ${token.access_token}`,
                    'content-type': 'multipart/form-data',
                },
            })        
            .then(res => {return res.data})
            .catch(err => console.log(err))       
            projectData["documents"] = fileInfo.data;
        }
        projectData.tags = fields.tags;

        handleClose();
        clearFields();
        setFileFormData(null);
        addProject(projectData);
    };

    const clearFields = () => {
        setFields({
            name: "",
            sponsor: "",
            contact: "",
            capacity: 6,
            tags: [],
            description: "",
        });
        setNewTag("");
    }

    const formatTag = (value) => {
        return value.split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
    }

    const updateForm = (tempFormData) => {
        setFileFormData(tempFormData);
    }

    useEffect(() => {
        async function loadTags() {
            const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/tags`,
            { headers: {Authorization: `Bearer ${token.access_token}`} })
            setTags(response.data)
        }
        loadTags();
    }, [open])

    useEffect(() => {
        if (selectedClass) {
            if (selectedClass.startSemester === "Fall")
                setEndSemester("Spring");
            if (selectedClass.startSemester === "Summer")
                setEndSemester("Fall");
        }
    }, [selectedClass])

    const semesters = ["Fall", "Spring", "Summer", "Summer/Fall"];

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        width: "60%",
        maxWidth: 550,
        minWidth: 300,
        maxHeight: 650,
        p: 3,
        borderRadius: 1,
        boxShadow: 24,
        overflowX: "auto",
    };

    return (
        <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        >
            <Box sx={modalStyle}>
                <Stack spacing={2} component="form" onSubmit={handleSubmit}>
                    <Typography variant="h4">Add Project</Typography>
                    <TextField required variant="outlined" id="project-name" label="Project Name" onChange={handleChange("name")}/>
                    <Stack direction="row" spacing={2}>
                        <TextField required variant="outlined" fullWidth id="sponsor" label="Sponsor" onChange={handleChange("sponsor")}/>
                        <TextField variant="outlined" fullWidth id="contact" label="Sponsor Contact" onChange={handleChange("sponsorContact")} />
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Autocomplete
                            id="start-semester"
                            options={semesters}
                            value={selectedClass.startSemester}
                            selectOnFocus
                            disabled
                            clearOnBlur
                            handleHomeEndKeys
                            autoHighlight
                            autoSelect
                            fullWidth
                            renderInput={(params) => (
                                <TextField {...params} label="Start Semester" variant="outlined"/>
                            )}
                        />
                        <Autocomplete
                            id="end-semester"
                            options={semesters}
                            value={endSemester}
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            autoHighlight
                            autoSelect
                            fullWidth
                            onChange={handleEndSemesterChange}
                            renderInput={(params) => (
                                <TextField {...params} label="End Semester" variant="outlined" required/>
                            )}
                        />
                    </Stack>
                    <Stack spacing={1}>
                        <Box style={{display: "flex", gap: 16, marginBottom: "10px", alignItems: "center"}}>
                            <TextField required type="number" variant="outlined" id="capacity" label="Size" value={fields.capacity} onChange={handleChange("capacity")}/>
                            <Autocomplete 
                                value={newTag}
                                options={tags}
                                isOptionEqualToValue={(option, value) => option.tag_id === value.tag_id}
                                getOptionLabel={(option) => option.name ? option.name : ""}
                                freeSolo
                                fullWidth
                                onInputChange={(event, value) => handleTagChange(value)}
                                onChange={(event, value) => addTag(value)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Add Custom Tag" variant="outlined"/>
                                )}
                            />
                            <Button variant="contained" onClick={handleNewTag}>Add</Button>
                        </Box>
                        <Box sx={{display: "flex", gap: 1, maxWidth: 500, flexWrap: "wrap"}}>
                            {fields.tags.map((tag) => ( 
                                <Chip label={tag.name} onDelete={() => removeTag(tag)} key={tag.name}/>
                            ))}
                        </Box>
                    </Stack>
                    <TextField variant="outlined" multiline minRows={4} id="description" label="Description" onChange={handleChange("description")} fullWidth/>
                    <FileUploadButton
                        type={"project_documents"}
                        selectedClass={selectedClass}
                        host={process.env.REACT_APP_API_HOST}
                        singleMultiple={"multiple"}
                        associatedGroupingType={"project"}
                        updateForm={updateForm}
                    />
                    <Stack direction="row" spacing={3} justifyContent="center">
                        <Button variant="outlined" onClick={handleClose} fullWidth>Cancel</Button>
                        <Button variant="contained" type="submit" fullWidth>Submit</Button>
                    </Stack>
                </Stack>
            </Box>
        </Modal>
    );
}

export default CreateProjectModal;
